import React, { useState } from 'react';
import { Gauge, FileJson, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Table = ({ title, rows, cols, testid }) => (
  <div className="space-y-1" data-testid={testid}>
    <div className="font-semibold text-gray-800 dark:text-gray-200">{title}</div>
    {rows.length === 0 ? <div className="text-gray-400">Καμία μέτρηση ακόμα</div> : (
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead><tr className="text-left text-gray-500">{cols.map(([k, l]) => <th key={k} className="pr-3 font-medium">{l}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-gray-100 dark:border-gray-800 align-top">
                {cols.map(([k, , fmt]) => <td key={k} className="pr-3 py-0.5 break-all">{fmt ? fmt(r[k], r) : String(r[k] ?? '')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const join = (v) => (Array.isArray(v) ? v.join(', ') : v);

const IndexDetail = ({ d }) => {
  const unused = (d.indexes || []).filter((i) => i.unused);
  const redundant = (d.indexes || []).filter((i) => i.redundant_prefix_of);
  const missing = d.slow_shapes_without_matching_index || [];
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded p-2 space-y-0.5" data-testid={`perf-index-${d.collection}`}>
      <div className="font-semibold">{d.collection} <span className="font-normal text-gray-500">· {d.docs?.toLocaleString('el-GR')} εγγραφές · {d.index_count} ευρετήρια ({d.index_mb} MB)</span></div>
      {unused.length > 0 && <div className="text-amber-600">Αχρησιμοποίητα: {unused.map((i) => i.name).join(', ')}</div>}
      {redundant.length > 0 && <div className="text-amber-600">Διπλά (καλύπτονται από άλλο): {redundant.map((i) => `${i.name} → ${i.redundant_prefix_of}`).join(', ')}</div>}
      {missing.length > 0 && <div className="text-red-600">Αργές αναζητήσεις χωρίς ευρετήριο: {missing.map((m) => `[${m.filter_keys.join(', ')}] ×${m.count}`).join(' · ')}</div>}
      {d.indexes_error && <div className="text-gray-400">{d.indexes_error}</div>}
    </div>
  );
};

export const PerfIndexPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/diagnostic/perf-report`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const exportJson = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    a.download = `perf-report-${data.server}-${new Date().toISOString().slice(0, 16)}.json`;
    a.click();
  };

  const el = data?.event_loop;
  const idx = data?.indexes;
  return (
    <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-2 text-[11px] space-y-3" data-testid="perf-index-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Gauge className="w-4 h-4 text-blue-500" />
        <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">Απόδοση & ευρετήρια</span>
        <button onClick={load} disabled={loading} className="px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors" data-testid="perf-report-load-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (data ? 'Ανανέωση' : 'Φόρτωση αναφοράς')}
        </button>
        {data && <button onClick={exportJson} className="px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-testid="perf-report-export-btn"><FileJson className="w-3 h-3 inline" /> JSON</button>}
        {error && <span className="text-red-600" data-testid="perf-report-error">Σφάλμα: {error}</span>}
      </div>
      {data && (
        <>
          {data.enabled === false && <div className="text-amber-600" data-testid="perf-report-disabled">Οι μετρήσεις απόδοσης είναι απενεργοποιημένες σε αυτόν τον server· εμφανίζονται μόνο τα ευρετήρια.</div>}
          <div className="text-gray-500" data-testid="perf-report-meta">
            Server <b>{data.server}</b> · μετρήσεις από τα τελευταία {Math.round(data.uptime_min)}΄ λειτουργίας του server
            · καθυστέρηση εξυπηρέτησης 5΄: μέση <b>{el.lag_5m_avg_ms ?? '—'} ms</b>, μέγιστη <b>{el.lag_5m_max_ms ?? '—'} ms</b>
            · παγώματα &gt;1 δευτ.: <b>{el.stalls_over_1s_total}</b>
          </div>
          <Table testid="perf-blocking-sites" title="Τι παγώνει τον server (>1 δευτ.)" rows={el.blocking_sites}
            cols={[['site', 'Σημείο κώδικα'], ['stalls', 'Φορές'], ['blocked_s', 'Συνολικά δευτ.']]} />
          <Table testid="perf-jobs" title="Προγραμματισμένες εργασίες (πιο βαριές πρώτα)" rows={data.scheduler_jobs}
            cols={[['name', 'Εργασία'], ['count', 'Τρεξίματα'], ['avg_ms', 'Μέσο ms'], ['max_ms', 'Μέγιστο ms'], ['total_s', 'Σύνολο δευτ.'], ['errors', 'Σφάλματα']]} />
          <Table testid="perf-api" title="Σελίδες / API (πιο βαριά πρώτα)" rows={data.api_endpoints}
            cols={[['name', 'Διαδρομή'], ['count', 'Κλήσεις'], ['avg_ms', 'Μέσο ms'], ['p95_ms', 'p95 ms'], ['max_ms', 'Μέγιστο ms'], ['total_s', 'Σύνολο δευτ.']]} />
          <Table testid="perf-mongo" title={`Αργές αναζητήσεις βάσης (>${data.slow_mongo_threshold_ms} ms)`} rows={data.slow_mongo_queries}
            cols={[['collection', 'Πίνακας'], ['op', 'Είδος'], ['filter_keys', 'Φίλτρο', join], ['sort_keys', 'Ταξινόμηση', join], ['count', 'Φορές'], ['avg_ms', 'Μέσο ms'], ['max_ms', 'Μέγιστο ms'], ['failed', 'Έληξαν']]} />
          {idx && (
            <div className="space-y-1.5" data-testid="perf-indexes">
              <div className="font-semibold text-gray-800 dark:text-gray-200">
                Ευρετήρια: <span data-testid="perf-indexes-total">{idx.indexes_total}</span> σε {idx.collections_total} πίνακες
                <span className="font-normal text-gray-500"> · «αχρησιμοποίητα» μετρά από την τελευταία επανεκκίνηση της βάσης</span>
              </div>
              {idx.detail.map((d) => <IndexDetail key={d.collection} d={d} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
};
